import React from "react";
import { customSanitize } from "../../../utils/customSanitize";
import { ExtendedLink } from "@/components/ExtededLink/ExtendedLink";
import { LinkListOptionsType } from "@/types/ModuleOptionsType";
import styles from "./LinkListBox.module.scss";
import { Button } from "@/components/Button/Button";
import { ContentTag } from "@/components/ContentTag/ContentTag";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG";
import {
  getBoxPadding,
  replaceExtendedLinkText,
  checkLinkAvailable,
  getShowBoxClasses,
} from "@/utils/infoBoxUtils";
import { IconGridLayoutOptionsType } from "@/modules/IconGridModule/IconGridBox/IconGridBox";
import { IconSizeType } from "@/modules/IconGridModule/IconGridBox/IconGridBox";
import Image from "next/image";
import { Modul_Linkliste } from "@/wp/types/acf-module-types";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";

type LinkListElement = Exclude<Modul_Linkliste["linklist"]["elements"], false>;
export type LinkListBoxElementType = LinkListElement[number] & {
  content?: string;
  location?: string[];
};

export type LinkListBoxType = "team" | "default";

type LinkListBoxProps = {
  element: LinkListBoxElementType | null;
  infoBoxOptions: LinkListOptionsType;
  show_cardbox: boolean;
  show_divider: boolean;
  heading_level: number;
  layoutOptions: IconGridLayoutOptionsType;
  boxType?: LinkListBoxType;
  skeleton?: boolean;
  fallbackIconUrl?: string;
};

const getIconSize = (icon_size: IconSizeType) => {
  return {
    "--infobox-icon-size-mobile": `${icon_size.mobile}rem`,
    "--infobox-icon-size-tablet": `${icon_size.tablet}rem`,
    "--infobox-icon-size-desktop": `${icon_size.desktop}rem`,
    "--infobox-icon-size-fullhd": `${icon_size.fullhd}rem`,
  };
};

export const LinkListBox = ({
  element,
  infoBoxOptions,
  show_cardbox,
  show_divider,
  heading_level,
  layoutOptions,
  boxType,
  skeleton,
  fallbackIconUrl,
}: LinkListBoxProps) => {
  const { getIcon, getLabel } = useGlobalContext();

  const {
    flex_direction_class,
    icon_align_class,
    row_direction_class,
    title_style,
    text_align_class,
  } = layoutOptions ?? {};

  const itemIsNE =
    element?.location !== undefined
      ? !element.location.includes("noble-bc")
      : undefined;

  /* jscpd:ignore-start */
  const effectiveShowCardbox = show_cardbox;
  const effectiveShowDivider = show_cardbox ? false : show_divider;

  const { show_extended_link, button_type, box } = infoBoxOptions ?? {};
  /* jscpd:ignore-end */
  const icon_size = infoBoxOptions.icon_size ?? null;

  const title = element?.title;
  const link = element?.link;
  const link_extended = element?.link_extended;

  const { show_border_radius, show_box_shadow, box_padding } = box;

  const showBowClasses = getShowBoxClasses(
    effectiveShowCardbox,
    effectiveShowDivider,
    show_border_radius,
    show_box_shadow,
  );

  const skeletonClass = skeleton ? "skeleton-element" : "";

  const skeletonStyle = skeleton
    ? { width: "60%", minHeight: "1.2em" }
    : undefined;
  const skeletonTitleStyle = skeleton
    ? { width: "80%", minHeight: "1.4em" }
    : undefined;

  const TITLE_JSX = (
    <>
      {(element?.tagline || skeleton) && (
        <span
          className={`infobox-tagline is-block ${skeletonClass}`}
          style={skeletonStyle}
          dangerouslySetInnerHTML={{
            __html: skeleton
              ? "&nbsp;"
              : customSanitize(element?.tagline ?? ""),
          }}
        />
      )}

      {(skeleton ||
        (title &&
          (button_type !== "link-in-title" ||
            !checkLinkAvailable(
              (show_extended_link ? link_extended : link) || false,
              show_extended_link,
            )))) && (
        <span
          role="heading"
          aria-level={heading_level}
          className={`infobox-title ${styles["title"]} is-block ${element?.content ? "" : "pb-0"} ${skeletonClass}`}
          style={skeletonTitleStyle}
          dangerouslySetInnerHTML={{
            __html: skeleton ? "&nbsp;" : customSanitize(title ?? ""),
          }}
        />
      )}
    </>
  );

  const svg = element?.svg || null;

  const IconJSX = (
    <>
      {(svg?.url || fallbackIconUrl) && (
        <div className={`infobox-icon ${styles["infobox-icon"]}`}>
          {svg?.url ? (
            <>
              {svg.mime_type === "image/svg+xml" && (
                <CustomSVG
                  src={svg.url}
                  wrapper="svg"
                  className="is-flex"
                  width={"100%"}
                  height={"100%"}
                  role="img"
                  aria-label={`${svg.title} icon`}
                />
              )}
              {svg.mime_type !== "image/svg+xml" && (
                <Image
                  src={svg.url}
                  alt={svg.alt}
                  sizes="96px"
                  height={svg.height}
                  width={svg.width}
                />
              )}
            </>
          ) : (
            <CustomSVG
              src={getIcon("liEventNobleBC")}
              wrapper="svg"
              width={"100%"}
              height={"100%"}
              aria-label={getLabel("aria_label_billetto_event_icon").replace(
                "$eventTitle",
                element?.title ?? "",
              )}
              aria-hidden
            />
          )}
        </div>
      )}
    </>
  );

  const ContentJSX = (
    <div className={`is-flex infobox-content ${styles["infobox-content"]}`}>
      <div className="h-fit w-full">
        {TITLE_JSX}

        {/* jscpd:ignore-start */}
        {title &&
          !show_extended_link &&
          link &&
          link.url &&
          button_type === "link-in-title" && (
            <Button
              role="button"
              className={`mb-0`}
              type={"secondary"}
              href={link.url}
              target={link.target === "" ? "_self" : link.target}
              aria-label={link.aria_label}
            >
              {link.title}
            </Button>
          )}

        {title &&
          button_type === "link-in-title" &&
          show_extended_link &&
          link_extended && (
            <ExtendedLink
              extendedLink={replaceExtendedLinkText(title, link_extended)}
              btnType={"secondary"}
              extraClass={`mt-0 infobox-title`}
            />
          )}
        {/* jscpd:ignore-end */}

        {element?.content && (
          <ContentTag
            extraClass={`infobox-text`}
            content={element.content ?? ""}
          />
        )}
      </div>

      {!show_extended_link &&
        link &&
        link.url &&
        button_type !== "link-in-title" && (
          <Button
            role="button"
            className="mt-0"
            href={link.url}
            type={button_type}
            target={link.target === "" ? "_self" : link.target}
            aria-label={link.aria_label}
          >
            {link.title}
          </Button>
        )}

      {show_extended_link &&
        link_extended &&
        button_type !== "link-in-title" && (
          <ExtendedLink extendedLink={link_extended} btnType={button_type} />
        )}
    </div>
  );

  if (boxType === "team") {
    return (
      <div
        style={{
          ...(getBoxPadding(box_padding) as React.CSSProperties),
          ...(icon_size && (getIconSize(icon_size) as React.CSSProperties)),
        }}
      >
        <div
          data-invert={itemIsNE}
          className={`${styles.infobox} ${styles["team-card"]} ${showBowClasses}`}
        >
          {IconJSX}
          <div className={styles["team-body"]}>
            <div className={styles["team-name"]}>
              {(element?.tagline || skeleton) && (
                <span
                  className={`infobox-tagline is-block ${skeletonClass}`}
                  style={skeletonStyle}
                  dangerouslySetInnerHTML={{
                    __html: skeleton
                      ? "&nbsp;"
                      : customSanitize(element?.tagline ?? ""),
                  }}
                />
              )}
              {(title || skeleton) && (
                <span
                  role="heading"
                  aria-level={heading_level}
                  className={`infobox-title ${styles["title"]} is-block ${skeletonClass}`}
                  style={skeletonTitleStyle}
                  dangerouslySetInnerHTML={{
                    __html: skeleton ? "&nbsp;" : customSanitize(title ?? ""),
                  }}
                />
              )}
            </div>
            {element?.content && (
              <ContentTag
                extraClass={`infobox-text`}
                content={element.content}
              />
            )}
            {((!show_extended_link && link && link.url) ||
              (show_extended_link && link_extended)) &&
              button_type !== "link-in-title" && (
                <div className={styles["team-btn"]}>
                  {!show_extended_link && link && link.url && (
                    <Button
                      role="button"
                      type={button_type}
                      href={link.url}
                      target={link.target === "" ? "_self" : link.target}
                      aria-label={link.aria_label}
                    >
                      {link.title}
                    </Button>
                  )}
                  {show_extended_link && link_extended && (
                    <ExtendedLink
                      extendedLink={link_extended}
                      btnType={button_type}
                    />
                  )}
                </div>
              )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        ...(getBoxPadding(box_padding) as React.CSSProperties),
        ...(icon_size && (getIconSize(icon_size) as React.CSSProperties)),
      }}
    >
      <div
        data-invert={itemIsNE}
        data-flex-direction={flex_direction_class}
        data-icon-align={icon_align_class}
        data-title-style={title_style}
        data-is-centered={
          text_align_class === "has-text-centered" &&
          flex_direction_class === "is-flex-direction-column"
            ? "true"
            : "false"
        }
        className={`${styles.infobox} ${
          !effectiveShowCardbox && effectiveShowDivider
            ? styles["has-divider"]
            : ""
        } ${showBowClasses}`}
      >
        {row_direction_class === "is-row-normal-desktop" && (
          <>
            {IconJSX}
            {ContentJSX}
          </>
        )}
        {row_direction_class === "is-row-reverse-desktop" && (
          <>
            {ContentJSX}
            {IconJSX}
          </>
        )}
      </div>
    </div>
  );
};
